import { useEffect, useRef, useState } from "react";
import { Inbox, MonitorSmartphone, ScanSearch, Keyboard, Printer, Copy, Link2, ImagePlus, X, Loader2, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { uploadFile } from "@/lib/files";
import { FileImage } from "@/components/files/FileFields";
import { DisplayThemeSettings } from "@/components/tpv/DisplayThemeSettings";
import { SHORTCUTS, getDevice, openCustomerDisplay, openDrawerManual, setDevice, remoteDisplayUrl } from "@/lib/tpv";
import { BigButton, Chip } from "@/components/ui-kit";

function DisplayAds({ t }) {
  const { view, dispatch, staff } = useApp();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const ads = view.shop?.display_ads || [];
  const owner = staff?.role === "owner";
  const save = (list) => dispatch("shop.update", { display_ads: list });
  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    if (!navigator.onLine) return toast.error(t("upload_offline"));
    setBusy(true);
    try { const ids = []; for (const f of files) ids.push((await uploadFile(f, "display")).id); save([...ads, ...ids]); toast.success(t("upload_done")); } catch { toast.error(t("upload_failed")); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl bg-secondary p-3 space-y-2" data-testid="tpv-display-ads">
      <div className="font-bold flex items-center gap-1.5"><ImagePlus className="w-4 h-4" />{t("display_ads")}<span className="block text-xs font-normal text-muted-foreground">{t("display_ads_hint")}</span></div>
      <div className="flex flex-wrap gap-2">
        {ads.map((id) => (
          <div key={id} className="relative w-28 h-20 rounded-lg overflow-hidden border border-border bg-black" data-testid="display-ad-thumb">
            <FileImage id={id} className="w-full h-full object-cover" />
            {owner && <button type="button" onClick={() => save(ads.filter((x) => x !== id))} className="absolute top-1 end-1 w-6 h-6 rounded-full bg-black/70 text-white grid place-items-center" data-testid="display-ad-remove"><X className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
        {owner && <button type="button" onClick={() => input.current?.click()} disabled={busy} className="w-28 h-20 rounded-lg border-2 border-dashed border-border bg-white grid place-items-center hover:border-primary" data-testid="display-ad-add">{busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6 text-muted-foreground" />}</button>}
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={pick} data-testid="display-ad-input" />
    </div>
  );
}

function RemoteDisplay({ t, owner }) {
  const [code, setCode] = useState(null);
  const [pwSet, setPwSet] = useState(false);
  const [pw, setPw] = useState("");
  const [on, setOn] = useState(localStorage.getItem("sp_remote_display") !== "0");
  useEffect(() => { api.get("/display/code", { params: { device_id: localStorage.getItem("sp_device") } }).then(({ data }) => { setCode(data.code); setPwSet(!!data.password_set); }).catch(() => {}); }, []);
  const url = code ? remoteDisplayUrl(code) : "";
  const copy = () => navigator.clipboard?.writeText(url).then(() => toast.success(t("copied")));
  const savePw = async () => { try { await api.put("/display/password", { password: pw }); setPwSet(true); setPw(""); toast.success(t("display_password_saved")); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-xl bg-secondary p-3 space-y-2" data-testid="tpv-remote-display">
      <label className="flex items-center justify-between gap-3 font-bold">
        <span className="flex items-center gap-1.5"><Link2 className="w-4 h-4" />{t("remote_display")}<span className="block text-xs font-normal text-muted-foreground">{t("remote_display_hint")}</span></span>
        <Switch checked={on} onCheckedChange={(v) => { setOn(v); localStorage.setItem("sp_remote_display", v ? "1" : "0"); }} data-testid="tpv-remote-display-switch" />
      </label>
      {on && code && (<>
        <div className="rounded-lg bg-white border border-border p-2 space-y-1.5" data-testid="tpv-display-password-box">
          <div className="text-xs font-bold flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />{t("display_password")} {pwSet ? <span className="px-1.5 rounded bg-[#E8F5EE] text-[#1F9D55]" data-testid="tpv-display-pw-set">{t("display_pw_set")}</span> : <span className="px-1.5 rounded bg-[#FFF7E6] text-[#9a5b00]" data-testid="tpv-display-pw-unset">{t("display_pw_unset")}</span>}</div>
          <p className="text-[11px] text-muted-foreground">{t("display_install_hint", { url: `${window.location.origin}/display` })}</p>
          {owner && <div className="flex gap-2"><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("display_password_ph")} dir="ltr" className="flex-1 h-9 rounded-lg border border-border px-2 text-sm" data-testid="tpv-display-pw-input" /><button type="button" disabled={pw.length < 4} onClick={savePw} className="h-9 px-3 rounded-lg bg-[#0B2A5B] text-white text-sm font-bold disabled:opacity-40" data-testid="tpv-display-pw-save">{pwSet ? t("display_pw_change") : t("save")}</button></div>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="bg-white p-2 rounded-lg border border-border" data-testid="tpv-display-qr"><QRCodeSVG value={url} size={120} level="M" /></div>
          <div className="space-y-1.5">
            <span className="text-xs text-muted-foreground block">{t("pairing_code")} · {t("scan_to_pair")}</span>
            <span className="inline-block font-mono text-2xl font-black tracking-[.3em] bg-white rounded px-3 py-1 border border-border" dir="ltr" data-testid="tpv-display-code">{code}</span>
            <div className="flex flex-wrap items-center gap-2">
              <a href={url} target="_blank" rel="noreferrer" className="text-xs underline break-all" dir="ltr" data-testid="tpv-display-url">{url}</a>
              <button type="button" onClick={copy} className="h-9 px-3 rounded border border-border bg-white text-sm font-bold flex items-center gap-1" data-testid="tpv-display-copy"><Copy className="w-4 h-4" />{t("copy_link")}</button>
            </div>
          </div>
        </div>
      </>)}
    </div>
  );
}

export default function TpvSettings() {
  const { t, staff } = useApp();
  const nav = useNavigate();
  const [d, setD] = useState(getDevice);
  const upd = (patch) => { const n = { ...d, ...patch }; setD(n); setDevice(n); };
  return (
    <section className="rounded-2xl bg-white border border-border p-4 space-y-3" data-testid="tpv-settings">
      <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Printer className="w-5 h-5" />{t("tpv_terminal")}</h2>
      <p className="text-sm text-muted-foreground">{t("tpv_printer_hint")}</p>
      <div className="rounded-xl bg-secondary p-3" data-testid="tpv-touch-mode">
        <div className="font-bold">{t("touch_mode")}</div>
        <div className="text-xs text-muted-foreground mb-2">{t("touch_hint")}</div>
        <div className="flex gap-2">{["auto", "on", "off"].map((m) => <Chip key={m} active={d.touch === m} onClick={() => upd({ touch: m })} data-testid={`tpv-touch-${m}`}>{t(`touch_${m}`)}</Chip>)}</div>
      </div>
      {[["autoPrint", "auto_print", "auto_print_hint"], ["drawerOnCash", "drawer_on_cash", "drawer_on_cash_hint"]].map(([k, l, h]) => (
        <label key={k} className="flex items-center justify-between gap-3 font-bold min-h-[52px]">
          <span>{t(l)}<span className="block text-xs font-normal text-muted-foreground">{t(h)}</span></span>
          <Switch checked={!!d[k]} onCheckedChange={(v) => upd({ [k]: v })} data-testid={`tpv-${k}-switch`} />
        </label>
      ))}
      <div className="grid sm:grid-cols-3 gap-2">
        <BigButton variant="soft" onClick={openDrawerManual} data-testid="tpv-test-drawer-btn"><Inbox className="w-5 h-5" />{t("open_drawer")}</BigButton>
        <BigButton variant="soft" onClick={openCustomerDisplay} data-testid="tpv-open-display-btn"><MonitorSmartphone className="w-5 h-5" />{t("customer_display")}</BigButton>
        <BigButton variant="soft" onClick={() => nav("/price-check")} data-testid="tpv-open-price-check-btn"><ScanSearch className="w-5 h-5" />{t("price_check")}</BigButton>
      </div>
      <RemoteDisplay t={t} owner={staff?.role === "owner"} />
      <DisplayAds t={t} />
      <DisplayThemeSettings />
      <div>
        <div className="font-bold flex items-center gap-1.5 mb-2"><Keyboard className="w-4 h-4" />{t("shortcuts")}</div>
        <div className="grid grid-cols-2 gap-1.5 text-sm">
          {SHORTCUTS.map(([k, l]) => <div key={k} className="flex gap-2"><kbd dir="ltr" className="min-w-14 text-center rounded bg-secondary font-bold text-xs py-0.5">{k}</kbd>{t(l)}</div>)}
        </div>
      </div>
    </section>
  );
}
